import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useResendVerification, useVerifyEmail } from '@/api/auth/use-auth';
import { Button } from '@/components/ui/button';

const VerifyEmailPage: React.FC = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { mutate: verifyEmail } = useVerifyEmail();
    const { mutate: resendVerification, isPending: isResending } = useResendVerification();

    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>(
        'pending'
    );

    useEffect(() => {
        if (token && email) {
            verifyEmail(
                { token, email },
                {
                    onSuccess: () => {
                        setVerificationStatus('success');
                        enqueueSnackbar(t('emailVerifiedSuccess'), { variant: 'success' });
                    },
                    onError: () => {
                        setVerificationStatus('error');
                        enqueueSnackbar(t('emailVerificationFailed'), { variant: 'error' });
                    },
                }
            );
        }
    }, [token, email]);

    const handleResendVerification = () => {
        if (email) {
            resendVerification(
                { email },
                {
                    onSuccess: () => {
                        enqueueSnackbar(t('verificationEmailSent'), { variant: 'success' });
                    },
                    onError: () => {
                        enqueueSnackbar(t('failedToSendVerification'), { variant: 'error' });
                    },
                }
            );
        }
    };

    if (!token || !email) {
        return (
            <>
                <h1 className="text-2xl font-bold text-center text-destructive mb-4">
                    {t('invalidVerificationLink')}
                </h1>
                <p className="text-center text-muted-foreground mb-6">
                    {t('invalidVerificationLinkDetail')}
                </p>
                <Button className="w-full" onClick={() => navigate('/auth/login')}>
                    {t('returnToLogin')}
                </Button>
            </>
        );
    }

    if (verificationStatus === 'pending') {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <h2 className="text-xl font-semibold mb-2">{t('verifyingEmail')}</h2>
                <p className="text-muted-foreground text-center">{t('pleaseWaitVerify')}</p>
            </div>
        );
    }

    if (verificationStatus === 'success') {
        return (
            <>
                <div className="bg-green-100 text-green-800 px-4 py-3 rounded mb-4 text-center">
                    {t('emailVerifiedSuccess')}
                </div>
                <p className="text-center text-muted-foreground mb-6">
                    {t('emailVerifiedSuccessDetail')}
                </p>
                <Button className="w-full" onClick={() => navigate('/auth/login')}>
                    {t('proceedToLogin')}
                </Button>
            </>
        );
    }

    return (
        <>
            <div className="bg-red-100 text-red-800 px-4 py-3 rounded mb-4 text-center">
                {t('emailVerificationFailed')}
            </div>
            <p className="text-center text-muted-foreground mb-4">
                {t('emailVerificationFailedReasons')}
            </p>
            <ul className="mb-6 list-disc list-inside text-sm text-muted-foreground">
                <li>The verification link has expired</li>
                <li>The verification link is invalid</li>
                <li>There was an error processing your request</li>
            </ul>
            <Button
                className="w-full mb-2"
                onClick={handleResendVerification}
                disabled={isResending}
            >
                {isResending ? t('sending') : t('resendVerificationEmail')}
            </Button>
            <Button className="w-full" variant="outline" onClick={() => navigate('/auth/login')}>
                {t('returnToLogin')}
            </Button>
        </>
    );
};

export default VerifyEmailPage;
